<script setup>
import { stringFormatter } from '@/helpers/utils'
import { computed, defineEmits, watch } from "vue";

const props = defineProps({
  label: { type: String, default: null},
  type: { type: String, default: 'text'},
  shouldValidate: { type: Boolean, default: false },
  validationFunction: { type: Function, default: null },
  validationType: { type: String, default: null },
  rangeMin: { type: Number, default: null },
  required: { type: Boolean, default: false },
  modelValue: { default: null },
});

const emit = defineEmits(['invalidField', 'update:modelValue', 'validated']);

const showError = computed(() => {
  return props.shouldValidate && !isValid.value;
});
const errorMessage = computed(() => {
  let message = 'Field is invalid';
  switch(props.validationType){
    case 'latitude':
      message = props.label + ' must be between -90.0 and 90.0';
      break;
    case 'longitude':
      message = props.label + ' must be between -180.0 and 180.0';
      break;
    case 'positive':
      message = props.label + ' cannot be negative';
      break;
    case 'year':
      message = props.label + ' cannot be negative or in the future';
      break;
    case 'range':
      message = props.label + ' must be larger than '+ props.rangeMin;
      break;
    default:
      message = props.label + ' is invalid';
  }
  if(props.required && props.modelValue === null){
    message = props.label + ' cannot be blank'
  }
  return stringFormatter.toSentenceCase(message);
});

const finishValidating = (result) => {
  const data = { valid: result, label: props.label.toLowerCase()};
  emit('validated', data);
};
const fallsWithinRange = (min, max) => {
  if(props.required && props.modelValue === null){
    return false;
  }
  return props.modelValue >= min && props.modelValue <= max;
};
const isPositive = () => {
  if(props.required && props.modelValue === null){
    return false;
  }
  return props.modelValue >= 0;
};
const isValidDate = () => {
  const valueMissing = props.modelValue === null;
  if(props.required && valueMissing){
    return false;
  }
  else if(valueMissing){
    return true;
  }
  const date = new Date(props.modelValue+'');
  const dateInFuture = date.getFullYear() <= new Date().getFullYear();
  // check if date is invalid first
  return !isNaN(date.getTime()) && dateInFuture;
};
const isLargerThan = (min) => {
  if(props.required && props.modelValue === null){
    return false;
  }
  return props.modelValue > min;
};

const isValid = computed(() => {
  if(props.required && (props.modelValue === null || props.modelValue === '')){
    finishValidating(false);
    return false;
  }
  if (props.validationFunction){
    const result = props.validationFunction(props.modelValue);
    finishValidating(result);
    return result;
  }
  // otherwise, go by validation type
  else if (props.validationType){
    let result = true;
    switch(props.validationType){
      case 'latitude':
        result = fallsWithinRange(-90, 90);
        break;
      case 'longitude':
        result =  fallsWithinRange(-180, 180);
        break;
      case 'positive':
        result = isPositive();
        break;
      case 'year':
        result = fallsWithinRange(0, new Date().getFullYear());
        break;
      case 'date':
        result = isValidDate();
        break;
      case 'range':
        result = isLargerThan(props.rangeMin);
        break;
      default:
        result = true;
    }
    finishValidating(result);
    return result;
  }
  finishValidating(true);
  return true;
});

watch(showError, (to) => {
  if(to){
    emit('invalidField', props.label.toLowerCase());
  }
});

const handleInput = (event) => {
  const value = event.target.value;
  emit('update:modelValue', value);
};

</script>

<template>
  <div class="validation-input">
    <label for="inputField" class="form-label">{{label}}</label>
    <input v-if="type === 'number'" :value="modelValue" type="number" :class="['form-control', {'invalid-field': showError}]"
           id="inputField" step="any" @input="handleInput">
    <input v-else :type="type" :value="modelValue" :class="['form-control', {'invalid-field': showError}]"
           id="inputField" @input="handleInput">
    <p v-if="showError" class="input-error">{{errorMessage}}</p>
  </div>
</template>

<style scoped>
.invalid-field {
  border-color: #843534;
  box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 6px #ce8483;
}
.input-error {
  color: #d9534f;
  float: right;
  font-size: x-small;
  margin-bottom: 0;
  margin-top: 0;
}
</style>